import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  createHttpLink,
  from,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import {
  CssBaseline,
  ThemeProvider as MuiThemeProvider,
  useMediaQuery,
} from '@mui/material';
import { BrowserRouter as Router } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ThemeProvider } from 'styled-components';
import './index.css';
import { IndexPage } from './modules/index-page';
import { Providers } from './providers';
import theme from './theme';
import { getGraphqlOperationUrl } from './utils/getGraphqlOperationUrl';

let host: string;
let httpProtocol: string;
const env = import.meta.env.VITE_HOST_ENV;

switch (env) {
  case 'dev':
  case 'development':
    host = 'dev-api.dishboard.app/graphql';
    httpProtocol = 'https://';
    break;
  case 'demo':
    host = 'demo-api.dishboard.app/graphql';
    httpProtocol = 'https://';
    break;
  case 'prod':
    host = 'api.dishboard.app/graphql';
    httpProtocol = 'https://';
    break;
  default:
    // Local dev answers on both `localhost` and `127.0.0.1`, which browsers
    // treat as different sites; following the address bar keeps the API
    // same-site so the auth cookie is sent.
    host = `${window.location.hostname}:4000/graphql`;
    httpProtocol = 'http://';
}

const uri = `${httpProtocol}${host}`;

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, extensions }) => {
      // Expired-session 401s are handled in auth.provider, but a rejected
      // login is also a 401 and its message is the only feedback the user gets.
      const isExpiredSession =
        // @ts-expect-error - better types later
        extensions?.response?.statusCode === 401 && message === 'Unauthorized';
      const isAdminMfaRequired =
        extensions?.code === 'ADMIN_MFA_REQUIRED' ||
        // @ts-expect-error - better types later
        extensions?.response?.error === 'ADMIN_MFA_REQUIRED';

      if (!isExpiredSession && !isAdminMfaRequired) {
        toast(message, { type: 'error', theme: 'colored' });
      }
    });
  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
    const { message } = networkError;
    toast(message, { type: 'warning', theme: 'colored' });
  }
});

const link = from([
  errorLink,
  createHttpLink({
    uri: ({ operationName }) =>
      getGraphqlOperationUrl({ graphqlUrl: uri, operationName }),
    credentials: 'include',
  }),
]);

const cache = new InMemoryCache({});

const client = new ApolloClient({
  cache,
  link,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-and-network',
    },
  },
});

const App = () => {
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <MuiThemeProvider theme={theme}>
      <ThemeProvider theme={theme}>
        <ApolloProvider client={client}>
          <CssBaseline />
          <ToastContainer position={isDesktop ? 'bottom-left' : 'top-right'} />
          <Router>
            <Providers>
              <IndexPage />
            </Providers>
          </Router>
        </ApolloProvider>
      </ThemeProvider>
    </MuiThemeProvider>
  );
};

export default App;
